import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
function getLogoBase64() {
  return new Promise((resolve, reject) => {
    fetch('/Proline Logo HQ.png')
      .then(r => r.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      })
      .catch(reject);
  });
}

const NAVY = [26, 26, 46];

const VALUATION_LABELS = {
  contract_rates: 'Contract Rates', new_rates: 'New Rates',
  fair_valuation: 'Fair Valuation', dayworks: 'Dayworks', mixed: 'Mixed',
};

function fmt(v) {
  if (v == null || v === '') return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v);
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export async function generateVariationPDF({ variation, project, instruction, revisions = [] }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 15;
  let y = 14;

  // Logo
  try {
    const logoDataUrl = await getLogoBase64();
    doc.addImage(logoDataUrl, 'PNG', margin, y, 28, 11);
  } catch (e) { /* no logo */ }

  // Company text right-aligned
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text('Proline Internals Ltd', W - margin, y + 4, { align: 'right' });
  doc.text('Commercial Management System', W - margin, y + 8, { align: 'right' });

  y += 18;

  // Navy header bar
  doc.setFillColor(...NAVY);
  doc.roundedRect(margin, y, W - margin * 2, 24, 2, 2, 'F');

  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184); // slate-400
  doc.text('VARIATION QUOTATION', margin + 4, y + 6);

  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(variation.reference || '—', margin + 4, y + 16);

  // Status top-right
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(203, 213, 225);
  doc.text((variation.status || '').replace(/_/g, ' ').toUpperCase(), W - margin - 4, y + 8, { align: 'right' });

  // Value top-right
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(fmt(variation.value), W - margin - 4, y + 18, { align: 'right' });

  y += 30;
  doc.setTextColor(55, 65, 81);
  doc.setFont(undefined, 'normal');

  // Title
  if (variation.title) {
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(26, 26, 46);
    doc.text(variation.title, margin, y);
    y += 7;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(55, 65, 81);
  }

  // Key info table
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 } },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 38, textColor: [100, 116, 139] },
      1: { cellWidth: 60 },
      2: { fontStyle: 'bold', cellWidth: 38, textColor: [100, 116, 139] },
      3: { cellWidth: 'auto' },
    },
    body: [
      ['Project', `${project?.reference || ''} — ${project?.name || ''}`, 'Valuation Basis', VALUATION_LABELS[variation.valuation_basis] || variation.valuation_basis || '—'],
      ['Instruction', instruction?.reference ? `${instruction.reference} (${instruction.type || ''})` : '—', 'Submitted Value', fmt(variation.value)],
      ['Submitted', variation.submitted_at ? fmtDate(variation.submitted_at) : 'Not yet submitted', 'MC Response', variation.mc_response ? variation.mc_response.replace(/_/g, ' ').toUpperCase() : 'Awaiting'],
      ['Submitted To', variation.submitted_to_name ? `${variation.submitted_to_name}${variation.submitted_to_company ? ', ' + variation.submitted_to_company : ''}` : '—', 'MC Agreed Value', variation.mc_response_value ? fmt(variation.mc_response_value) : '—'],
    ],
    theme: 'plain',
    tableLineColor: [229, 231, 235],
    tableLineWidth: 0.3,
  });

  y = doc.lastAutoTable.finalY + 8;

  // Description
  if (variation.description) {
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('DESCRIPTION OF WORKS', margin, y);
    y += 4;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(55, 65, 81);
    const descLines = doc.splitTextToSize(variation.description, W - margin * 2);
    doc.text(descLines, margin, y);
    y += descLines.length * 4.5 + 5;
  }

  // Scope notes
  if (variation.scope_notes) {
    if (y > H - 60) { doc.addPage(); y = 20; }
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('SCOPE NOTES', margin, y);
    y += 4;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(55, 65, 81);
    const scopeLines = doc.splitTextToSize(variation.scope_notes, W - margin * 2);
    doc.text(scopeLines, margin, y);
    y += scopeLines.length * 4.5 + 5;
  }

  // Revision history
  if (revisions.length > 0) {
    if (y > H - 60) { doc.addPage(); y = 20; }
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('REVISION HISTORY', margin, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
      head: [['Rev', 'Date Submitted', 'Value', 'Status']],
      body: revisions.map(r => [
        `Rev ${r.revision_number}`,
        r.submitted_at ? fmtDate(r.submitted_at) : '—',
        fmt(r.value),
        (r.status || '').replace(/_/g, ' ').toUpperCase(),
      ]),
      theme: 'striped',
      alternateRowStyles: { fillColor: [249, 250, 251] },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // MC Response
  if (variation.mc_response) {
    if (y > H - 40) { doc.addPage(); y = 20; }
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text('MC RESPONSE', margin, y);
    y += 4;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(55, 65, 81);
    const responseText = `${variation.mc_response.replace(/_/g, ' ').toUpperCase()}${variation.mc_response_value ? ' — ' + fmt(variation.mc_response_value) : ''}`;
    doc.text(responseText, margin, y);
    if (variation.mc_response_reason) {
      y += 5;
      const reasonLines = doc.splitTextToSize(variation.mc_response_reason, W - margin * 2);
      doc.text(reasonLines, margin, y);
      y += reasonLines.length * 4.5;
    }
  }

  // Footer on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const ph = doc.internal.pageSize.getHeight();
    doc.setDrawColor(229, 231, 235);
    doc.line(margin, ph - 14, W - margin, ph - 14);
    doc.setFontSize(7.5);
    doc.setTextColor(156, 163, 175);
    doc.setFont(undefined, 'normal');
    doc.text('Proline Internals Ltd — Commercial Management System', margin, ph - 8);
    doc.text(`Page ${i} of ${pageCount}`, W - margin, ph - 8, { align: 'right' });
    doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, W / 2, ph - 8, { align: 'center' });
  }

  doc.save(`${variation.reference || 'variation'}-VQ.pdf`);
}
